'use client';

import React, { useMemo, useState, useEffect } from 'react';
import { Box, Typography, Button, CircularProgress, Paper, Fade } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { useEstimator } from '../../context/EstimatorContext';
import useSaveCostAssumptions from '../../hooks/useSaveCostAssumptions';

const orderedTypes = ['Tax Rate', 'Contingency', 'Overhead', 'Row $/ft'];

const CostAssumptionsGrid = () => {
  const { formData, setFormData } = useEstimator();
  const saveCostAssumptions = useSaveCostAssumptions();
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  const [assumptions, setAssumptions] = useState(() => {
    const initial = formData.costAssumptions || [];

    const defaults = [
      { type: 'Tax Rate', value: '' },
      { type: 'Contingency', value: '' },
      { type: 'Overhead', value: '' },
    ];

    const merged = [...initial, ...defaults].filter(
      (item, index, self) => index === self.findIndex((i) => i.type === item.type)
    );

    const withRow = [...merged, { type: 'Row $/ft', value: formData.averageROWCostPerFoot ?? '' }];

    const sorted = withRow.sort((a, b) => orderedTypes.indexOf(a.type) - orderedTypes.indexOf(b.type));

    return sorted;
  });

  useEffect(() => {
    if (formData.averageROWCostPerFoot != null) {
      setAssumptions((prev) =>
        prev.map((a) => (a.type === 'Row $/ft' ? { ...a, value: formData.averageROWCostPerFoot } : a))
      );
    }
  }, [formData.averageROWCostPerFoot]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: 'Type',
        field: 'type',
        editable: false,
        cellStyle: { backgroundColor: '#f0f0f0', color: '#333' },
        flex: 1,
      },
      {
        headerName: 'Value',
        field: 'value',
        editable: (params) => params.data.type !== 'Row $/ft',
        flex: 1,
        valueFormatter: (params) => {
          const val = parseFloat(params.value);
          if (params.data.type === 'Row $/ft') {
            return isNaN(val) ? '' : `$${val.toFixed(2)}`;
          }
          return isNaN(val) ? '' : `${val}%`;
        },
        valueParser: (params) => {
          const raw = params.newValue?.toString().replace('%', '').trim();
          const num = parseFloat(raw);
          return isNaN(num) ? '' : num;
        },
        cellStyle: (params) =>
          params.data.type === 'Row $/ft'
            ? { backgroundColor: '#f0f0f0', color: '#999' }
            : { backgroundColor: '#ffffff', color: '#000' },
      },
    ],
    []
  );

  const handleGridEdit = (event) => {
    const updated = [...assumptions];
    updated[event.node.rowIndex] = event.data;
    setAssumptions(updated);
    setFormData((prev) => ({ ...prev, costAssumptions: updated }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const assumptionsToSave = assumptions.filter((a) => a.type !== 'Row $/ft');
      await saveCostAssumptions({
        estimateId: formData.id || formData.estimateId,
        assumptions: assumptionsToSave,
      });

      setSaved(true);
      setTimeout(() => setSaved(false), 3000); // Reset after 3 sec
    } catch (err) {
      console.error('Failed to save cost assumptions:', err);
      alert('Failed to save cost assumptions.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fade in>
      <Box mt={4} display="flex" flexDirection="column" gap={2}>
        <Typography
          variant="h6"
          sx={{
            backgroundColor: '#A7B9D1',
            color: 'white',
            padding: '8px 16px',
            borderRadius: 1,
          }}
        >
          Cost Assumptions
        </Typography>

        <Paper elevation={3} sx={{ p: 3, backgroundColor: '#F9F9F9', borderRadius: 2 }}>
          <AgGridReact
            rowData={assumptions}
            columnDefs={columnDefs}
            defaultColDef={{ resizable: true, cellStyle: { textAlign: 'left' } }}
            onCellValueChanged={handleGridEdit}
            domLayout="autoHeight"
            suppressMovableColumns
          />
        </Paper>

        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            startIcon={loading && <CircularProgress size={18} color="inherit" />}
            sx={{
              backgroundColor: saved ? '#4CAF50' : '#4A90E2',
              '&:hover': {
                backgroundColor: saved ? '#43A047' : '#357ABD',
              },
            }}
          >
            {loading ? 'Saving...' : saved ? 'Saved!' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Fade>
  );
};

export default CostAssumptionsGrid;
